import React, { createContext, useContext, useState, ReactNode } from 'react'
import { MapMode } from '../components'
import type { StartingPoint } from '../components/RouteStartPointSelector'
import type { WaypointResponse } from '@/shared/api/routes'

export type TimelineWaypoint = Partial<WaypointResponse> & {
  id: string
  name?: string | string[]
  latitude?: number
  longitude?: number
  order_index?: number | null
  source_type?: string
  source?: string
}

interface MapPageContextValue {
  // Panel state
  activePanel: string | null
  selectedRoute: any | null
  selectedWaypoint: any | null
  selectedPOI: any | null
  showDetailsPanel: boolean
  mode: MapMode
  timelineWaypoints: TimelineWaypoint[]
  isActivelyBuildingRoute: boolean
  startingPoint: StartingPoint | null
  
  // Dialog state
  dialogs: {
    showAssociateDialog: boolean
    showPOISearch: boolean
    showPOIDetails: boolean
    showCustomWaypoint: boolean
    showAddWaypoint: boolean
  }
  
  // Actions
  setActivePanel: (panel: string | null) => void
  setSelectedRoute: (route: any | null) => void
  setSelectedWaypoint: (waypoint: any | null) => void
  setSelectedPOI: (poi: any | null) => void
  setShowDetailsPanel: (show: boolean) => void
  setMode: (mode: MapMode) => void
  setTimelineWaypoints: React.Dispatch<React.SetStateAction<TimelineWaypoint[]>>
  setIsActivelyBuildingRoute: (value: boolean) => void
  setStartingPoint: (point: StartingPoint | null) => void
  
  // Dialog actions
  setShowAssociateDialog: (show: boolean) => void
  setShowPOISearch: (show: boolean) => void
  setShowPOIDetails: (show: boolean) => void
  setShowCustomWaypoint: (show: boolean) => void
  setShowAddWaypoint: (show: boolean) => void
}

const MapPageContext = createContext<MapPageContextValue | null>(null)

interface MapPageProviderProps {
  children: ReactNode
  initialMode?: MapMode
}

/**
 * Provider for map page panel state
 * Separates panel state from map data to prevent map layer updates when panels change
 */
export function MapPageProvider({ children, initialMode = 'browse' }: MapPageProviderProps) {
  const [activePanel, setActivePanel] = useState<string | null>(null)
  const [selectedRoute, setSelectedRoute] = useState<any | null>(null)
  const [selectedWaypoint, setSelectedWaypoint] = useState<any | null>(null)
  const [selectedPOI, setSelectedPOI] = useState<any | null>(null)
  const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false)
  const [mode, setMode] = useState<MapMode>(initialMode)
  const [timelineWaypoints, setTimelineWaypoints] = useState<TimelineWaypoint[]>([])
  const [isActivelyBuildingRoute, setIsActivelyBuildingRoute] = useState<boolean>(false)
  const [startingPoint, setStartingPoint] = useState<StartingPoint | null>(null)
  
  const [showAssociateDialog, setShowAssociateDialog] = useState(false)
  const [showPOISearch, setShowPOISearch] = useState(false)
  const [showPOIDetails, setShowPOIDetails] = useState(false)
  const [showCustomWaypoint, setShowCustomWaypoint] = useState(false)
  const [showAddWaypoint, setShowAddWaypoint] = useState(false)

  const value: MapPageContextValue = {
    activePanel,
    selectedRoute,
    selectedWaypoint,
    selectedPOI,
    showDetailsPanel,
    mode,
    timelineWaypoints,
    isActivelyBuildingRoute,
    startingPoint,
    dialogs: {
      showAssociateDialog,
      showPOISearch,
      showPOIDetails,
      showCustomWaypoint,
      showAddWaypoint
    },
    setActivePanel,
    setSelectedRoute,
    setSelectedWaypoint,
    setSelectedPOI,
    setShowDetailsPanel,
    setMode,
    setTimelineWaypoints,
    setIsActivelyBuildingRoute,
    setStartingPoint,
    setShowAssociateDialog,
    setShowPOISearch,
    setShowPOIDetails,
    setShowCustomWaypoint,
    setShowAddWaypoint
  }

  return (
    <MapPageContext.Provider value={value}>
      {children}
    </MapPageContext.Provider>
  )
}

/**
 * Hook to access map page context
 */
export function useMapPageContext(): MapPageContextValue {
  const context = useContext(MapPageContext)
  if (!context) {
    throw new Error('useMapPageContext must be used within MapPageProvider')
  }
  return context
}

